import type { useGT } from 'gt-react';
import type { TroubleData } from '@erp/shared-types';
import {
  computeLineTotalMismatchTroubleData,
  parseLineTotalMismatchComment,
  type InvoiceLineTotalAssessment,
} from '@erp/shared-utils';

type Translate = ReturnType<typeof useGT>;

const MISMATCH_CONTEXT =
  'AP invoice whose line items do not add up to the document total';

/** Sentence shown on the total, the issues panel, and the Accept tooltip. */
export function lineTotalMismatchMessage(
  assessment: InvoiceLineTotalAssessment,
  gt: Translate,
): string {
  if (
    assessment.lineTotal == null ||
    assessment.countedTax == null ||
    assessment.documentTotal == null
  ) {
    return gt('Invoice line items do not add up to the invoice total.', {
      context: MISMATCH_CONTEXT,
    });
  }
  if (assessment.countedTax !== '0.00') {
    return gt(
      'Invoice line items total {lineTotal} plus tax {tax}, which does not add up to the invoice total of {documentTotal}.',
      {
        context: MISMATCH_CONTEXT,
        lineTotal: assessment.lineTotal,
        tax: assessment.countedTax,
        documentTotal: assessment.documentTotal,
      },
    );
  }
  return gt(
    'Invoice line items total {lineTotal}, which does not add up to the invoice total of {documentTotal}.',
    {
      context: MISMATCH_CONTEXT,
      lineTotal: assessment.lineTotal,
      documentTotal: assessment.documentTotal,
    },
  );
}

/** Translate the machine comment stored on a LINE_TOTAL_MISMATCH entry. */
export function formatStoredLineTotalMismatchComment(
  comment: string,
  gt: Translate,
): string {
  const parsed = parseLineTotalMismatchComment(comment);
  const unreadable =
    !parsed ||
    parsed.lineTotal === '?' ||
    parsed.countedTax === '?' ||
    parsed.documentTotal === '?';
  if (unreadable || !parsed) {
    return lineTotalMismatchMessage(
      {
        addsUp: false,
        lineTotal: null,
        countedTax: null,
        documentTotal: null,
      },
      gt,
    );
  }
  return lineTotalMismatchMessage(
    {
      addsUp: false,
      lineTotal: parsed.lineTotal,
      countedTax: parsed.countedTax,
      documentTotal: parsed.documentTotal,
    },
    gt,
  );
}

/**
 * Keep the header badge in step with the amounts on screen. A stored flag is
 * removed when the lines now agree, and added when they do not, without
 * waiting for the next save.
 */
export function overlayLineTotalTrouble(
  current: TroubleData,
  assessment: InvoiceLineTotalAssessment,
  currency?: string | null,
): TroubleData {
  return computeLineTotalMismatchTroubleData(current, assessment, currency);
}
